import React, { ReactElement, useContext } from 'react';
import moment from 'moment';
import { Card, List } from 'antd';
import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/client';
import ProgressModal from '../../../components/ProgressModal';
// import message from '../../../components/message';

import './index.css';

import AppContext from '../../../components/AppContext';
import PageTitle from '../../../components/PageTitle';
import Pullable from '../../../components/Pullable';
import { MutationSetNotificationsReadArgs, Notification, NotificationsResult, NotificationType, QueryNotificationsArgs } from '../../../utils/types';

const NOTIFICATIONS_QUERY = gql`
  query NotificationsQuery($skip: Int, $count: Int) {
    notifications(skip: $skip, count: $count) {
      total
      totalUnread
      skip
      list {
        id
        type
        createdAt
        firstReadAt

        shippingGroup {
          id
          name
        }

        package {
          id
          trackingNumber
          shippingCompany
          weight
          items
          length
          width
          depth
          warehouseNotes
        }

        order {
          id
          number
          chargeWeight
          totalPrice
          warehouseNotes
          shippingGroup {
            id
            name
            hasFixedLeader
          }
          packages {
            id
          }
        }
      }
    }
  }
`;
const READ_NOTIFICATION_MUTATION = gql`
  mutation readNotificationMutation($ids: [ID!]!) {
    setNotificationsRead(ids: $ids)
  }
`;

const renderNotification = (notification: Notification): ReactElement | undefined => {
  const { type, package: pkg, order, shippingGroup } = notification;

  switch (type) {
    case NotificationType.PackageReceivedAtWarehouse:
      if (!pkg) {
        return <div>你预报的包裹已入库并称重，包裹：【包裹已删除，请忽略本通知】</div>;
      }
      return (
        <div>
          你预报的包裹已入库并称重，包裹：【{pkg.shippingCompany} {pkg.trackingNumber}】，物品：
          {pkg.items}，实际重量：
          {pkg.weight?.toFixed(2)}kg
          {pkg['length'] &&
            pkg.width &&
            pkg.depth &&
            `，尺寸(cm)：${pkg['length']?.toFixed(0)}×${pkg['width']?.toFixed(0)}×${pkg[
              'depth'
            ]?.toFixed(0)}（可能会产生体积重）`}
          {pkg.warehouseNotes && `，仓库备注：${pkg.warehouseNotes}`}
        </div>
      );
    case NotificationType.ProblemPackageReceivedAtWarehouse:
      if (!pkg) {
        return <div>你预报的包裹被标记为问题件，包裹：【包裹已删除，请忽略本通知】</div>;
      }
      return (
        <div>
          你预报的包裹被标记为问题件，包裹：【{pkg.shippingCompany} {pkg.trackingNumber}】，物品：
          {pkg.items}，实际重量：
          {pkg.weight?.toFixed(2)}kg
          {pkg.warehouseNotes && `，仓库备注：${pkg.warehouseNotes}`}，问题件通常需要联系客服处理
        </div>
      );
    case NotificationType.NewPackageReceivedAtWarehouse:
      if (!pkg) {
        return <div>你有未预报的包裹入库并称重，包裹：【包裹已删除，请忽略本通知】</div>;
      }
      return (
        <div>
          你有未预报的包裹入库并称重，包裹：【{pkg.shippingCompany} {pkg.trackingNumber}】，物品：
          {pkg.items}，实际重量：
          {pkg.weight?.toFixed(2)}kg
          {pkg['length'] &&
            pkg.width &&
            pkg.depth &&
            `，尺寸(cm)：${pkg['length']?.toFixed(0)}×${pkg['width']?.toFixed(0)}×${pkg[
              'depth'
            ]?.toFixed(0)}（可能会产生体积重）`}
          {pkg.warehouseNotes && `，仓库备注：${pkg.warehouseNotes}`}
        </div>
      );
    case NotificationType.NewProblemPackageReceivedAtWarehouse:
      if (!pkg) {
        return <div>你有未预报的包裹被标记为问题件，包裹：【包裹已删除，请忽略本通知】</div>;
      }
      return (
        <div>
          你有未预报的包裹被标记为问题件，包裹：【{pkg.shippingCompany} {pkg.trackingNumber}】，物品：
          {pkg.items}，实际重量：
          {pkg.weight?.toFixed(2)}kg
          {pkg.warehouseNotes && `，仓库备注：${pkg.warehouseNotes}`}，问题件通常需要联系客服处理
        </div>
      );

    case NotificationType.ShippingGroupOrderCreated:
      if (!order) {
        return (
          <div>
            你参加的拼邮团【拼邮订单已删除，请忽略本通知】已截团，你被自动设置为收货团长（包裹总重量最大），请尽快到我的订单页面提交收货地址，有问题请联系客服
          </div>
        );
      }
      if (order?.shippingGroup?.hasFixedLeader) {
        return (
          <div>
            你参加的拼邮团【
            {order?.shippingGroup?.name}
            】已截团，你作为固定团长，包裹会寄到你家，麻烦到货时建群组织大家取货
          </div>
        );
      }
      return (
        <div>
          你参加的拼邮团【
          {order?.shippingGroup?.name}
          】已截团，你被自动设置为收货团长（包裹总重量最大），请尽快到我的订单页面提交收货地址，有问题请联系客服
        </div>
      );

    case NotificationType.OrderPaymentPending:
      if (!order) {
        return (
          <div>
            你的订单运费已出，请尽快支付，支付请微信或支付宝转账给客服。订单号：【订单已删除，请忽略本通知】
          </div>
        );
      }
      if (order?.shippingGroup) {
        return (
          <div>
            你参加的拼邮团运费已出，请尽快支付，支付请微信或支付宝转账给客服。订单号：【
            {order?.number}】，拼邮团：【
            {order?.shippingGroup.name}】，计费重量：
            {order?.chargeWeight?.toFixed(2)}kg，总费用：￥{order?.totalPrice}
            {order?.warehouseNotes && `，仓库备注：${order?.warehouseNotes}`}
          </div>
        );
      } else {
        return (
          <div>
            你的直邮订单运费已出，请尽快支付，支付请微信或支付宝转账给客服。订单号：【
            {order?.number}
            】，计费重量：
            {order?.chargeWeight?.toFixed(2)}kg，总费用：￥{order?.totalPrice}
            {order?.warehouseNotes && `，仓库备注：${order?.warehouseNotes}`}
          </div>
        );
      }

    case NotificationType.OrderShipped:
      if (!order) {
        return <div>你的订单已发出，订单号：【订单已删除，请忽略本通知】</div>;
      }
      if (order?.shippingGroup) {
        return (
          <div>
            你的拼邮订单已发出，订单号：【{order?.number}】，拼邮团：【{order?.shippingGroup?.name}
            】，包裹数量：
            {order?.packages?.length}
            {order?.warehouseNotes && `，仓库备注：${order?.warehouseNotes}`}
          </div>
        );
      } else {
        return (
          <div>
            你的直邮订单已发出，订单号：【{order?.number}】，包裹数量：{order?.packages?.length}
            {order?.warehouseNotes && `，仓库备注：${order?.warehouseNotes}`}
          </div>
        );
      }
    case NotificationType.ShippingGroupOrderPickupPending:
      if (!order) {
        return (
          <div>你的拼邮包裹已到达团长处，请上门取货。拼邮团：【拼邮订单已删除，请忽略本通知】</div>
        );
      }
      return (
        <div>
          你的拼邮包裹已到达团长处，请上门取货，取货前请先联系团长进入取货群，团长信息和取货地址请查看订单详情。拼邮团：【
          {order?.shippingGroup?.name}】，包裹数量：
          {order?.packages?.length}
        </div>
      );
    case NotificationType.PackageRemovedFromOrder:
      if (!pkg) {
        return <div>你的包裹被移出订单，包裹：【包裹已删除，请忽略本通知】</div>;
      }
      if (shippingGroup) {
        return (
          <div>
            你的包裹被移出拼邮团：【{shippingGroup.name}
            】，包裹：【{pkg.shippingCompany} {pkg.trackingNumber}】，物品：
            {pkg.items}，仓库备注：{pkg.warehouseNotes}，如有疑问请联系客服
          </div>
        );
      } else {
        return (
          <div>
            你的包裹被移出直邮订单，包裹：【{pkg.shippingCompany} {pkg.trackingNumber}】，物品：
            {pkg.items}，仓库备注：{pkg.warehouseNotes}，如有疑问请联系客服
          </div>
        );
      }
  }
};

function Notifications() {
  const { setNotificationsTotalUnread } = useContext(AppContext);
  const { loading, data = { notifications: { list: [], total: 0 } }, refetch } = useQuery<{ notifications: NotificationsResult }, QueryNotificationsArgs>(
    NOTIFICATIONS_QUERY,
    {
      variables: {},
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        setNotificationsTotalUnread(data.notifications.totalUnread);
      },
    },
  );

  const [readNotification] = useMutation<{ setNotificationsRead: number }, MutationSetNotificationsReadArgs>(READ_NOTIFICATION_MUTATION, {
    onError: () => null,
    onCompleted: data => {
      ProgressModal.hide();
      refetch();
    },
  });

  return (
    <div className="Notifications">
      <PageTitle>系统通知</PageTitle>
      <Pullable onRefresh={refetch}>
        <List
          loading={loading && !data}
          grid={{ gutter: 16, column: 1 }}
          dataSource={data?.notifications?.list}
          renderItem={(notification) => {
            const content = renderNotification(notification);
            if (!content) return null;

            return (
              <Card
                bordered={false}
                key={notification.id}
                style={{
                  marginBottom: '0.8rem',
                  background: notification.firstReadAt ? 'white' : '#EB6841',
                  color: notification.firstReadAt ? '#888' : 'white',
                }}
                onClick={() => {
                  if (!notification.firstReadAt)
                    readNotification({ variables: { ids: [notification.id] } });
                }}
              >
                {content}
                <div
                  className="Notifications-footer"
                  style={{ color: notification.firstReadAt ? '#CCC' : 'white' }}
                >
                  {moment(notification.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                </div>
              </Card>
            );
          }}
        ></List>
      </Pullable>
    </div>
  );
}

export default Notifications;
